import type { BoolLike } from './types';

/**
 * Low-level value parsing shared by the schedule module: day ids, booleans,
 * wall-clock times and dates. Everything here is timezone-naive — schedule
 * columns hold the zone's local wall-clock with no offset.
 */

/**
 * Parse `delimited_day_ids` ("2,4,6") into sorted, de-duplicated day ids.
 * Anything that is not an integer from 1 (Sunday) to 7 (Saturday) is dropped.
 */
export function decodeDayIds(value: string | null | undefined): number[] {
    if (!value) {
        return [];
    }
    const dayIds = new Set<number>();
    for (const part of String(value).split(',')) {
        const trimmed = part.trim();
        if (!/^\d+$/.test(trimmed)) {
            continue;
        }
        const dayId = Number(trimmed);
        if (dayId >= 1 && dayId <= 7) {
            dayIds.add(dayId);
        }
    }
    return [...dayIds].sort((left, right) => left - right);
}

/**
 * Encode day ids for `delimited_day_ids`: sorted ascending, comma-joined, no
 * spaces. Returns null when no valid day remains. All seven is "1,2,3,4,5,6,7".
 */
export function encodeDayIds(dayIds: readonly number[]): string | null {
    const valid = [...new Set(dayIds)]
        .filter((dayId) => Number.isInteger(dayId) && dayId >= 1 && dayId <= 7)
        .sort((left, right) => left - right);
    return valid.length > 0 ? valid.join(',') : null;
}

export function toBool(value: BoolLike): boolean | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'number') {
        return value !== 0;
    }
    const lowered = value.trim().toLowerCase();
    if (lowered === '1' || lowered === 'true') {
        return true;
    }
    if (lowered === '0' || lowered === 'false') {
        return false;
    }
    return null;
}

/** A positive integer, or null for null, zero, negative or non-numeric values. */
export function positiveInteger(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return null;
    }
    const whole = Math.trunc(numeric);
    return whole >= 1 ? whole : null;
}

export interface ClockTime {
    hours: number;
    minutes: number;
    seconds: number;
}

/** Parse "HH:MM" or "HH:MM:SS" (24-hour). Returns null when it is not a time. */
export function parseClockTime(value: string | null | undefined): ClockTime | null {
    if (!value) {
        return null;
    }
    const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(String(value).trim());
    if (!match) {
        return null;
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = match[3] === undefined ? 0 : Number(match[3]);
    if (hours > 23 || minutes > 59 || seconds > 59) {
        return null;
    }
    return { hours, minutes, seconds };
}

function pad2(value: number): string {
    return String(value).padStart(2, '0');
}

/** Normalise a time to the backend's `H:i:s` format ("06:00" -> "06:00:00"). */
export function normalizeClockTime(value: string | null | undefined): string | null {
    const parsed = parseClockTime(value);
    if (!parsed) {
        return null;
    }
    return `${pad2(parsed.hours)}:${pad2(parsed.minutes)}:${pad2(parsed.seconds)}`;
}

/**
 * A time on a 12-hour clock, as the time field shows it, in the backend's
 * `H:i:s` format. 12 AM is midnight ("00:00:00") and 12 PM is noon.
 */
export function clockTimeFromTwelveHour(time: { hour: number; minute: number; period: 'AM' | 'PM' }): string {
    const hours = (time.hour % 12) + (time.period === 'PM' ? 12 : 0);
    return `${pad2(hours)}:${pad2(time.minute)}:00`;
}

function clockSeconds(time: ClockTime): number {
    return time.hours * 3600 + time.minutes * 60 + time.seconds;
}

/**
 * True when a daily window ends at or before it starts, so it ends the next
 * day. The players treat end <= start that way (physicalplayer
 * checkValidEndTimeCustomRecurring; gvplayer adds 86400 s for messaging).
 * False when either time is missing.
 */
export function timesCrossMidnight(
    startTime: string | null | undefined,
    endTime: string | null | undefined,
): boolean {
    const start = parseClockTime(startTime);
    const end = parseClockTime(endTime);
    if (!start || !end) {
        return false;
    }
    return clockSeconds(end) <= clockSeconds(start);
}

export interface CalendarDate {
    year: number;
    month: number;
    day: number;
}

/**
 * Parse the leading "YYYY-MM-DD" of a date string. Returns null for anything
 * else, including impossible dates such as 2026-02-30.
 */
export function parseCalendarDate(value: string | null | undefined): CalendarDate | null {
    if (!value) {
        return null;
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
    if (!match) {
        return null;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const probe = new Date(year, month - 1, day);
    if (probe.getFullYear() !== year || probe.getMonth() !== month - 1 || probe.getDate() !== day) {
        return null;
    }
    return { year, month, day };
}

/** "YYYY-MM-DD" for a valid date string, else null. */
export function normalizeCalendarDate(value: string | null | undefined): string | null {
    const parsed = parseCalendarDate(value);
    if (!parsed) {
        return null;
    }
    return `${parsed.year}-${pad2(parsed.month)}-${pad2(parsed.day)}`;
}

/** The calendar day after `value` ("2026-12-31" -> "2027-01-01"). */
export function nextCalendarDate(value: string): string | null {
    const parsed = parseCalendarDate(value);
    if (!parsed) {
        return null;
    }
    const next = new Date(parsed.year, parsed.month - 1, parsed.day + 1);
    return `${next.getFullYear()}-${pad2(next.getMonth() + 1)}-${pad2(next.getDate())}`;
}

/**
 * Read a naive wall-clock datetime as a local Date. Accepts
 * "YYYY-MM-DD HH:MM:SS", "YYYY-MM-DDTHH:MM:SS" with any fraction or offset
 * (the offset is ignored, see ScheduleRow.effective_start_datetime), or a bare
 * "YYYY-MM-DD" (midnight). A Date instance is read through its UTC fields,
 * because the backend labels the wall-clock value as UTC.
 */
export function parseWallClock(value: string | Date | null | undefined): Date | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) {
            return null;
        }
        return new Date(
            value.getUTCFullYear(),
            value.getUTCMonth(),
            value.getUTCDate(),
            value.getUTCHours(),
            value.getUTCMinutes(),
            value.getUTCSeconds(),
        );
    }
    const date = parseCalendarDate(value);
    if (!date) {
        return null;
    }
    const timePart = /^\d{4}-\d{2}-\d{2}[T ](\d{2}:\d{2}(?::\d{2})?)/.exec(value.trim());
    const time = timePart ? parseClockTime(timePart[1]) : { hours: 0, minutes: 0, seconds: 0 };
    if (!time) {
        return null;
    }
    return new Date(date.year, date.month - 1, date.day, time.hours, time.minutes, time.seconds);
}

/** Combine a date column and a time column as the database trigger does. */
export function combineWallClock(
    date: string | null | undefined,
    time: string | null | undefined,
): Date | null {
    const parsedDate = parseCalendarDate(date);
    if (!parsedDate) {
        return null;
    }
    const parsedTime = parseClockTime(time) ?? { hours: 0, minutes: 0, seconds: 0 };
    return new Date(
        parsedDate.year,
        parsedDate.month - 1,
        parsedDate.day,
        parsedTime.hours,
        parsedTime.minutes,
        parsedTime.seconds,
    );
}

/** Whether the browser knows this IANA time zone name. */
export function isKnownTimeZone(timeZone: string): boolean {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone });
        return true;
    } catch {
        return false;
    }
}

/**
 * The wall-clock time at a fixed offset from UTC (seconds east), as a local
 * Date whose fields read that clock. For a time zone the browser does not know
 * by name. It has no daylight saving, which is what a fixed offset means.
 */
export function wallClockAtOffset(instant: Date, offsetSeconds: number): Date {
    const shifted = new Date(instant.getTime() + offsetSeconds * 1000);
    return new Date(
        shifted.getUTCFullYear(),
        shifted.getUTCMonth(),
        shifted.getUTCDate(),
        shifted.getUTCHours(),
        shifted.getUTCMinutes(),
        shifted.getUTCSeconds(),
    );
}

/**
 * The wall-clock time in `timeZone` at `instant`, as a local Date whose fields
 * (getFullYear … getSeconds) read that zone's clock. Schedule dates and times
 * are naive zone-local values, so this is the `now` to compare them with
 * (amendment A6). An unknown or empty zone name returns the instant unchanged,
 * which is the browser's own clock.
 */
export function wallClockInTimeZone(instant: Date, timeZone: string | null | undefined): Date {
    if (!timeZone) {
        return new Date(instant.getTime());
    }
    let parts: Intl.DateTimeFormatPart[];
    try {
        parts = new Intl.DateTimeFormat('en-US', {
            timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        }).formatToParts(instant);
    } catch {
        // RangeError: a zone name the browser does not know.
        return new Date(instant.getTime());
    }
    const field = (type: Intl.DateTimeFormatPartTypes): number =>
        Number(parts.find((part) => part.type === type)?.value ?? 0);
    return new Date(field('year'), field('month') - 1, field('day'), field('hour'), field('minute'), field('second'));
}
